/**
 * SignallingClient — WebSocket signalling client for live streaming.
 * Connects to /v1/stream/signal?token=JWT
 * Handles all message types from spec §5.2
 * Heartbeat: ping every 10s, reconnect on missed pong (15s timeout)
 * Auto-reconnect: 3 attempts with exponential backoff
 * Clean teardown on stop/navigate-away
 */

export type ClientMessage =
  | { type: 'join'; prompt: string; session_id: string; timezone?: string }
  /** PRI-281: pre-connect — join without prompt. Prompt sent later via set_prompt + DC. */
  | { type: 'connect'; session_id: string; timezone?: string }
  /** PRI-281: pre-connect — sends real prompt to Railway for DB logging after WebRTC is up. */
  | { type: 'set_prompt'; session_id: string; prompt: string }
  | { type: 'offer'; sdp: string }
  | { type: 'ice'; candidate: RTCIceCandidateInit }
  | { type: 'stop' }
  | { type: 'ping' }

export type InferenceResult = {
  answer?: string
  confidence?: number
  query_type?: string
  search_term?: string
  inference_ms?: number
  elapsed_s?: number
  session_fps?: number
  frame_num?: number
  [key: string]: unknown
}

export type ServerMessage =
  | { type: 'auth_ok' }
  | { type: 'queued'; position: number; estimated_wait_s: number }
  | { type: 'position_update'; position: number; estimated_wait_s: number }
  | { type: 'ready'; recording_max_minutes?: number | null }
  | { type: 'answer'; sdp: string }
  | { type: 'ice'; candidate: RTCIceCandidateInit }
  | { type: 'streaming'; elapsed_s: number; session_remaining_s: number; daily_remaining_s: number }
  | { type: 'quota_warning'; session_remaining_s: number; daily_remaining_s: number; message: string }
  | { type: 'session_ended'; duration_s: number; daily_remaining_s: number; message: string }
  | { type: 'quota_exceeded'; daily_used_s: number; message: string }
  | { type: 'stopped'; duration_s: number; reason: string }
  | { type: 'error'; code: string; message: string }
  | { type: 'pong' }
  | { type: 'metadata'; frame_num: number; detections: InferenceResult[] }
  | { type: 'status'; status: string; message: string }

/**
 * PRI-421: diagnostics passed to onDisconnected after reconnect attempts are
 * exhausted. Lets callers distinguish "signal WS never reached the server"
 * (everOpened=false — the 2026-07-02 prod blank-results signature) from
 * "established connection lost mid-run", and report it to Sentry/analytics.
 */
export type SignallingDiagnostics = {
  everOpened: boolean
  everAuthed: boolean
  attempts: number
  lastCloseCode: number | null
  lastCloseReason: string | null
}

export interface SignallingClientOptions {
  url: string
  token: string | null
  onQueued: (position: number, estimatedWaitSec: number) => void
  onPositionUpdate: (position: number, estimatedWaitSec: number) => void
  onReady: (recordingMaxMinutes: number | null | undefined) => void
  onAnswer: (sdp: string) => void
  onIce: (candidate: RTCIceCandidateInit) => void
  onStreaming: (elapsedSec: number, sessionRemainingSec: number, dailyRemainingSec: number) => void
  onQuotaWarning: (sessionRemainingSec: number, dailyRemainingSec: number) => void
  onSessionEnded: (durationSec: number, dailyRemainingSec: number) => void
  onQuotaExceeded: () => void
  onStopped: (durationSec: number, reason: string) => void
  onError: (code: string, message: string) => void
  onDisconnected: (diag?: SignallingDiagnostics) => void
  // Called with the JEPA inference result for each sampled frame.
  // detections[0] from the sidecar is the flat inference dict.
  onMetadata?: (frameNum: number, inference: InferenceResult) => void
  onStatus?: (status: string, message: string) => void
  // Timing instrumentation callbacks
  onWsOpen?: () => void
  onAuthOk?: () => void
}

/**
 * PRI-496 Lane B: the structural contract useStreamSession programs against.
 * Satisfied by BOTH the legacy SignallingClient (internal /v1/stream/signal)
 * and the PublicStreamSignalling adapter (public /v1/streams plane) so the
 * hook can swap planes behind the stream-plane kill-switch without forking
 * its 2,500-line orchestration.
 */
export interface ISignallingClient {
  readonly isAuthenticated: boolean
  connect(sessionId: string, prompt: string, timezone: string): void
  connectNoPrompt(sessionId: string, timezone: string): void
  setPromptViaWS(prompt: string): void
  setOptions(partial: Partial<SignallingClientOptions>): void
  send(msg: ClientMessage): void
  disconnect(): void
}

const PING_INTERVAL_MS = 10_000
const PONG_TIMEOUT_MS = 15_000
// PRI-421: 3 attempts (~7s) gave up faster than a transient edge/WS-proxy
// episode lasts. 5 attempts with the delay capped at 8s ≈ 23s of retrying.
const MAX_RECONNECT_ATTEMPTS = 5
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_DELAY_MS = 8_000

export class SignallingClient {
  private options: SignallingClientOptions
  private ws: WebSocket | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private pongTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempts = 0
  private sessionId = ''
  private prompt = ''
  private timezone = ''
  private stopped = false
  /** PRI-281: true when this is a pre-connect session (no prompt at join time). */
  private noPrompt = false
  // Whether auth_ok has been received (PRI-178)
  get isAuthenticated(): boolean { return this._auth }  
  private _auth = false
  private authTimer: ReturnType<typeof setTimeout> | null = null
  // PRI-421: lifetime diagnostics for terminal-disconnect reporting
  private everOpened = false
  private everAuthed = false
  private lastCloseCode: number | null = null
  private lastCloseReason: string | null = null

  constructor(options: SignallingClientOptions) {
    this.options = options
  }

  /** PRI-281: pre-connect — establish WebRTC without a prompt.
   *  The real prompt is sent later via setPromptViaWS() + DC initial_prompt message. */
  connectNoPrompt(sessionId: string, timezone: string): void {
    this.sessionId = sessionId
    this.prompt = ''
    this.timezone = timezone
    this.noPrompt = true
    this.stopped = false
    this.reconnectAttempts = 0
    this.openWebSocket()
  }

  /** PRI-281: tell Railway about the real prompt (for DB logging).
   *  Must be called after WebRTC is established. The sidecar receives the
   *  prompt directly via the data channel (initial_prompt DC message). */
  setPromptViaWS(prompt: string): void {
    const msg: ClientMessage = { type: 'set_prompt', session_id: this.sessionId, prompt }
    console.log('[Stream] Sending set_prompt for session', this.sessionId)
    this.send(msg)
  }

  connect(sessionId: string, prompt: string, timezone: string): void {
    this.sessionId = sessionId
    this.prompt = prompt
    this.noPrompt = false
    this.timezone = timezone
    this.stopped = false
    this.reconnectAttempts = 0
    this.openWebSocket()
  }

  private openWebSocket(): void {
    // PRI-178: Connect to bare URL — token sent as first message, never in URL
    const url = this.options.url
    console.log('[Stream] WebSocket connecting to:', url)
    this.ws = new WebSocket(url)
    this._auth = false

    this.ws.onopen = () => {
      console.log('[Stream] WebSocket connected, readyState:', this.ws?.readyState)
      this.everOpened = true // PRI-421
      this.options.onWsOpen?.()
      this.reconnectAttempts = 0
      this.startHeartbeat()
      // Send auth as first message (PRI-178).
      // PRI-433 P4: the server no longer accepts anonymous (empty-token)
      // sessions — an empty token gets error{code:unauthorized} + close.
      // Non-empty token = Clerk JWT for authenticated session.
      const authToken = this.options.token ?? ''
      this.ws!.send(JSON.stringify({ type: 'auth', token: authToken }))
      // Wait up to 8s for auth_ok before considering connection failed
      this.authTimer = setTimeout(() => {
        console.error('[Stream] Auth timeout — no auth_ok received')
        this.options.onError('auth_timeout', 'Authentication timed out')
        this.ws?.close()
      }, 8_000)
    }

    this.ws.onmessage = (event) => {
      console.log('[Stream] Message received:', (event.data as string).slice(0, 100))
      let msg: ServerMessage
      try {
        msg = JSON.parse(event.data as string) as ServerMessage
      } catch {
        return
      }
      this.handleMessage(msg)
    }

    this.ws.onerror = (e) => {
      console.error('[Stream] WebSocket error:', e)
    }

    this.ws.onclose = (e) => {
      console.log('[Stream] WebSocket closed, code:', e.code, 'reason:', e.reason, 'wasClean:', e.wasClean)
      this.lastCloseCode = e.code ?? null // PRI-421
      this.lastCloseReason = e.reason || null
      this.stopHeartbeat()
      if (!this.stopped) {
        this.attemptReconnect()
      }
    }
  }

  private sendJoin(): void {
    // PRI-281: pre-connect sessions send 'connect' (no prompt) instead of 'join'.
    const joinMsg: ClientMessage = this.noPrompt
      ? { type: 'connect', session_id: this.sessionId, timezone: this.timezone }
      : { type: 'join', prompt: this.prompt, session_id: this.sessionId, timezone: this.timezone }
    console.log('[Stream] Sending', joinMsg.type, ':', JSON.stringify(joinMsg).slice(0, 100))
    this.send(joinMsg)
  }

  private handleMessage(msg: ServerMessage): void {
    if (msg.type === 'auth_ok') {
      if (this.authTimer) { clearTimeout(this.authTimer); this.authTimer = null }
      this._auth = true
      this.everAuthed = true // PRI-421
      this.options.onAuthOk?.()
      console.log('[Stream] Auth OK — sending join')
      this.sendJoin()
      return
    }
    if (msg.type === 'pong') {
      if (this.pongTimer) { clearTimeout(this.pongTimer); this.pongTimer = null }
      return
    }
    if (msg.type === 'queued') {
      this.options.onQueued(msg.position, msg.estimated_wait_s)
    } else if (msg.type === 'position_update') {
      this.options.onPositionUpdate(msg.position, msg.estimated_wait_s)
    } else if (msg.type === 'ready') {
      this.options.onReady(msg.recording_max_minutes)
    } else if (msg.type === 'answer') {
      this.options.onAnswer(msg.sdp)
    } else if (msg.type === 'ice') {
      this.options.onIce(msg.candidate)
    } else if (msg.type === 'streaming') {
      this.options.onStreaming(msg.elapsed_s, msg.session_remaining_s, msg.daily_remaining_s)
    } else if (msg.type === 'quota_warning') {
      this.options.onQuotaWarning(msg.session_remaining_s, msg.daily_remaining_s)
    } else if (msg.type === 'session_ended') {
      this.options.onSessionEnded(msg.duration_s, msg.daily_remaining_s)
    } else if (msg.type === 'quota_exceeded') {
      this.options.onQuotaExceeded()
    } else if (msg.type === 'stopped') {
      this.options.onStopped(msg.duration_s, msg.reason)
    } else if (msg.type === 'error') {
      this.options.onError(msg.code, msg.message)
    } else if (msg.type === 'metadata') {
      // JEPA result is wrapped in a single-element detections array by the sidecar.
      const inference = msg.detections?.[0]
      if (inference) this.options.onMetadata?.(msg.frame_num, inference)
    } else if (msg.type === 'status') {
      console.log('[PRI-263] WS received status message:', msg.status, msg.message)
      this.options.onStatus?.(msg.status, msg.message)
    }
  }

  private startHeartbeat(): void {
    this.pingTimer = setInterval(() => {
      this.send({ type: 'ping' })
      this.pongTimer = setTimeout(() => {
        console.log('[Stream] Pong timeout — connection lost')
        this.ws?.close()
      }, PONG_TIMEOUT_MS)
    }, PING_INTERVAL_MS)
  }

  private stopHeartbeat(): void {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null }
    if (this.pongTimer) { clearTimeout(this.pongTimer); this.pongTimer = null }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.log('[Stream] Max reconnect attempts reached')
      // PRI-421: report HOW it died so callers can log/track it. Before this,
      // a run whose WS never reached the server produced zero telemetry
      // anywhere (2026-07-02 prod blank-results incident).
      this.options.onDisconnected({
        everOpened: this.everOpened,
        everAuthed: this.everAuthed,
        attempts: this.reconnectAttempts,
        lastCloseCode: this.lastCloseCode,
        lastCloseReason: this.lastCloseReason,
      })
      return
    }
    const delay = Math.min(RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts), RECONNECT_MAX_DELAY_MS)
    this.reconnectAttempts++
    console.log(`[Stream] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`)
    setTimeout(() => {
      if (!this.stopped) this.openWebSocket()
    }, delay)
  }

  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg))
    }
  }

  /** PRI-281: Replace inference callbacks on a pre-connected SignallingClient.
   *  Called from startStream() fast path to wire real handlers onto the pre-connect WS. */
  setOptions(partial: Partial<SignallingClientOptions>): void {
    this.options = { ...this.options, ...partial }
  }

  disconnect(): void {
    this.stopped = true
    this.stopHeartbeat()
    if (this.authTimer) { clearTimeout(this.authTimer); this.authTimer = null }
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
  }
}
